import { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import NotFound from "../modules/error-pages/NotFound";
import LoadingPage from "../modules/loading/LoadingPage";
import MasterLayout from "../custom-components/master-layout/MasterLayout";
import ParentLayout from "../pages/parentWorkSpace/ParentLayout";
import StudentLayout from "../pages/studentWorkSpace/StudentLayout";
import TeacherLayout from "../pages/teacherWorkSpace/TeacherLayout";
import ParentTeacherLayout from "../pages/parentTeacherWorkSpace/ParentTeacherLayout";
import {
  appRoutes,
  parentRoutes,
  parentNavigations,
  studentRoutes,
  studentNavigations,
  teacherNavigations,
  teacherRoutes,
  parentTeacherNavigations,
  parentTeacherRoutes,
} from "../../setup/route_config";
import AdminLayout from "../pages/adminWorkSpace/AdminLayout";
import adminNavigations, { adminRoutes } from "../../setup/route_config/adminRoutes";
import RegistrarLayout from "../pages/registrarWorkSpace/RegistrarLayout";
import registrarNavigations, { registrarRoutes } from "../../setup/route_config/registrarRoutes";
import { useEffect } from "react";
import SharedLayout from "@components/pages/shared/SharedLayout";
import { useAuth } from "../../context/AuthContext";

// LAZY PAGES
const LoginPage = lazy(() => import("../pages/authentication/LoginPage"));
const SetPasswordPage = lazy(() => import("../pages/authentication/SetPasswordPage"));
const ForgotPasswordPage = lazy(() => import("../pages/authentication/ForgotPasswordPage"));

// Role-prefixed URL segments. A logged-in user with `baseRole === X` is only
// allowed under `/X/...`; any other role prefix here triggers a redirect to
// their own workspace's known landing path. Keep in sync with the role
// workspaces below. The fully-qualified landing avoids relying on the
// `<Route index>` Navigate chain, which doesn't reliably fire when the
// outer URL was on a foreign role's path that never registered.
const ROLE_PATH_PREFIXES = [
  "admin",
  "teacher",
  "parent-teacher",
  "parent",
  "student",
  "registrar",
] as const;

const ROLE_LANDING: Record<string, string> = {
  admin: "/admin/dashboard",
  teacher: "/teacher",
  "parent-teacher": "/parent-teacher",
  parent: "/parent",
  student: "/student",
  registrar: "/registrar/students",
  // SuperAdmin lives entirely under /root — never under any non-root path.
  // The actual JWT base_role value is the kebab-case "super-admin".
  "super-admin": "/root",
};

const Routing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { baseRole, loading, session } = useAuth();

  useEffect(() => {
    // Public auth pages (set / forgot password) must stay reachable while
    // logged out — the emailed recovery link lands on /set-password BEFORE
    // Supabase opens the recovery session, so a naive redirect would bounce the
    // user away before they can set their password.
    const publicPaths = ["/set-password", "/forgot-password"];
    if (!loading && !session && !publicPaths.includes(location.pathname)) navigate("/");
  }, [loading, session, navigate, location.pathname]);

  // SuperAdmin trampoline: App.tsx picks Routing vs RootRouting based on
  // `window.location.pathname` ONCE on initial render. If a logged-in
  // super-admin somehow lands on a non-/root path (e.g. they typed the
  // bare domain or clicked an external link), the index Route below
  // renders <Navigate to="/root">, which changes the URL via react-router
  // but does NOT re-trigger App.tsx's branch decision — we stay inside
  // <Routing>, /root doesn't match any of these routes, and the
  // session-aware catch-all on line ~442 renders <NotFound>. Bounce out
  // with a full reload so App re-evaluates the path and mounts
  // <RootRouting> instead. (Mirrors the inverse bounce in
  // RootRouting.tsx that protects /root from non-super-admins.)
  useEffect(() => {
    if (loading || !session || baseRole !== "super-admin") return;
    if (window.location.pathname === "/root" || window.location.pathname.startsWith("/root/")) return;
    window.location.replace("/root");
  }, [loading, session, baseRole]);

  // Cross-role guard: if an authenticated user lands on another role's URL
  // prefix (e.g. student visiting /admin/dashboard), redirect them to their
  // own workspace. Without this, the route block for the foreign role is
  // never registered (see conditional Route trees below) and the URL just
  // sits on the forbidden path showing a NotFound — the URL never changes,
  // so cross-role e2e access checks fail.
  useEffect(() => {
    if (loading || !session || !baseRole) return;

    const segments = location.pathname.split("/").filter(Boolean);
    const firstSegment = segments[0];
    if (!firstSegment) return;

    const isRolePrefix = (ROLE_PATH_PREFIXES as readonly string[]).includes(firstSegment);
    if (!isRolePrefix) return; // shared/app routes (e.g. /profile) are fine

    if (firstSegment !== baseRole) {
      const landing = ROLE_LANDING[baseRole] ?? `/${baseRole}`;
      navigate(landing, { replace: true });
    }
  }, [loading, session, baseRole, location.pathname, navigate]);

  return (
    <Suspense fallback={<LoadingPage />}>
      <Routes>
        {loading && <Route path="*" element={<LoadingPage />} />}

        {!loading && (
          <>
            {/* Use session/baseRole (JWT claims) so routing doesn't wait for DB profile */}
            <Route
              index
              element={
                session ? (
                  // SuperAdmin lives under /root and gets bounced via
                  // window.location.replace in the useEffect above —
                  // keep rendering LoadingPage during the reload so
                  // there's no NotFound flicker (a react-router
                  // <Navigate to="/root"> would change the URL inside
                  // <Routing> but not switch App.tsx to RootRouting,
                  // landing on the session-aware catch-all → NotFound).
                  baseRole === "super-admin" ? (
                    <LoadingPage />
                  ) : (
                    <Navigate to={ROLE_LANDING[baseRole as string] ?? `/${baseRole}`} />
                  )
                ) : (
                  <LoginPage />
                )
              }
            />

            {/* Public auth pages — reachable while logged out; the emailed
                recovery link lands on /set-password before Supabase opens the
                session (see the redirect-exemption effect above). */}
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* PARENT */}
            {baseRole === "parent" && (
              <Route
                path="/parent"
                element={
                  <MasterLayout>
                    <ParentLayout />
                  </MasterLayout>
                }
              >
                <Route
                  index
                  element={
                    <Navigate
                      to={
                        studentNavigations.flatMap((item) => item.routes).find((item) => item.index)
                          ?.path ?? ""
                      }
                    />
                  }
                />

                {parentNavigations.map((group) =>
                  group.routes.map((route, index) => (
                    <Route key={index} path={route.path} element={route.component}>
                      {(route?.children ?? []).map((sub_route, sub_index) => (
                        <Route
                          key={sub_index}
                          path={sub_route.path}
                          element={sub_route.component}
                          index={sub_route.index}
                        />
                      ))}
                    </Route>
                  )),
                )}

                {/* NESTED route in WORKSPACE */}
                {parentRoutes.map((r, index) => (
                  <Route key={"parent" + index} path={r.path} element={r.component} />
                ))}
              </Route>
            )}

            {/* TEACHER */}
            {baseRole === "teacher" && (
              <Route
                path="/teacher"
                element={
                  <MasterLayout>
                    <TeacherLayout />
                  </MasterLayout>
                }
              >
                <Route
                  index
                  element={
                    <Navigate
                      to={
                        studentNavigations.flatMap((item) => item.routes).find((item) => item.index)
                          ?.path ?? ""
                      }
                    />
                  }
                />

                {teacherNavigations.map((group) =>
                  group.routes.map((route, index) => (
                    <Route key={index} path={route.path} element={route.component}>
                      {(route?.children ?? []).map((sub_route, sub_index) => (
                        <Route
                          key={sub_index}
                          path={sub_route.path}
                          element={sub_route.component}
                          index={sub_route.index}
                        />
                      ))}
                    </Route>
                  )),
                )}

                {teacherRoutes.map((r, index) =>
                  r.index ? (
                    <Route index element={r.component} key="index" />
                  ) : (
                    <Route key={"teacher" + index} path={r.path} element={r.component}>
                      {r.children?.map((child, childIndex) => (
                        <Route
                          key={`teacher-child-${index}-${childIndex}`}
                          path={child.path}
                          element={child.component}
                          index={child.index}
                        />
                      ))}
                    </Route>
                  ),
                )}
              </Route>
            )}

            {/* PARENT TEACHER */}
            {baseRole === "parent-teacher" && (
              <Route
                path="/parent-teacher"
                element={
                  <MasterLayout>
                    <ParentTeacherLayout />
                  </MasterLayout>
                }
              >
                <Route
                  index
                  element={
                    <Navigate
                      to={
                        studentNavigations.flatMap((item) => item.routes).find((item) => item.index)
                          ?.path ?? ""
                      }
                    />
                  }
                />

                {parentTeacherNavigations.map((group) =>
                  group.routes.map((route, index) => (
                    <Route key={index} path={route.path} element={route.component}>
                      {(route?.children ?? []).map((sub_route, sub_index) => (
                        <Route
                          key={sub_index}
                          path={sub_route.path}
                          element={sub_route.component}
                          index={sub_route.index}
                        />
                      ))}
                    </Route>
                  )),
                )}

                {parentTeacherRoutes.map((r, index) =>
                  r.index ? (
                    <Route index element={r.component} key="index" />
                  ) : (
                    <Route key={"teacher" + index} path={r.path} element={r.component}>
                      {r.children?.map((child, childIndex) => (
                        <Route
                          key={`teacher-child-${index}-${childIndex}`}
                          path={child.path}
                          element={child.component}
                          index={child.index}
                        />
                      ))}
                    </Route>
                  ),
                )}
              </Route>
            )}

            {/* ADMIN */}
            {baseRole === "admin" && (
              <Route
                path="/admin"
                element={
                  <MasterLayout>
                    <AdminLayout />
                  </MasterLayout>
                }
              >
                <Route
                  index
                  element={
                    <Navigate
                      to={
                        studentNavigations.flatMap((item) => item.routes).find((item) => item.index)
                          ?.path ?? ""
                      }
                    />
                  }
                />

                {adminNavigations.map((group) =>
                  group.routes.map((route, index) => (
                    <Route key={index} path={route.path} element={route.component}>
                      {(route?.children ?? []).map((sub_route, sub_index) => (
                        <Route
                          key={sub_index}
                          path={sub_route.path}
                          element={sub_route.component}
                          index={sub_route.index}
                        />
                      ))}
                    </Route>
                  )),
                )}

                {/* NESTED route in WORKSPACE */}
                {adminRoutes.map((r, index) =>
                  r.index ? (
                    <Route index element={r.component} key="index" />
                  ) : (
                    <Route key={"teacher" + index} path={r.path} element={r.component}>
                      {r.children?.map((child, childIndex) => (
                        <Route
                          key={`teacher-child-${index}-${childIndex}`}
                          path={child.path}
                          element={child.component}
                          index={child.index}
                        />
                      ))}
                    </Route>
                  ),
                )}
              </Route>
            )}

            {/* STUDENT */}
            {baseRole === "student" && (
              <Route
                path="/student"
                element={
                  <MasterLayout>
                    <StudentLayout />
                  </MasterLayout>
                }
              >
                <Route
                  index
                  element={
                    <Navigate
                      to={
                        studentNavigations.flatMap((item) => item.routes).find((item) => item.index)
                          ?.path ?? ""
                      }
                    />
                  }
                />

                {studentNavigations.map((group) =>
                  group.routes.map((route, index) => (
                    <Route key={index} path={route.path} element={route.component}>
                      {(route?.children ?? []).map((sub_route, sub_index) => (
                        <Route
                          key={sub_index}
                          path={sub_route.path}
                          element={sub_route.component}
                          index={sub_route.index}
                        />
                      ))}
                    </Route>
                  )),
                )}

                {/* NESTED route in WORKSPACE */}
                {studentRoutes.map((r, index) => (
                  <Route key={"student" + index} path={r.path} element={r.component} />
                ))}
              </Route>
            )}

            {/* REGISTRAR */}
            {baseRole === "registrar" && (
              <Route
                path="/registrar"
                element={
                  <MasterLayout>
                    <RegistrarLayout />
                  </MasterLayout>
                }
              >
                <Route
                  index
                  element={
                    <Navigate
                      to={
                        registrarNavigations
                          .flatMap((item) => item.routes)
                          .find((item) => item.index)?.path ?? "students"
                      }
                    />
                  }
                />

                {registrarNavigations.map((group) =>
                  group.routes.map((route, index) => (
                    <Route key={index} path={route.path} element={route.component}>
                      {(route?.children ?? []).map((sub_route, sub_index) => (
                        <Route
                          key={sub_index}
                          path={sub_route.path}
                          element={sub_route.component}
                          index={sub_route.index}
                        />
                      ))}
                    </Route>
                  )),
                )}

                {registrarRoutes.map((r, index) =>
                  r.index ? (
                    <Route index element={r.component} key="index" />
                  ) : (
                    <Route key={"teacher" + index} path={r.path} element={r.component}>
                      {r.children?.map((child, childIndex) => (
                        <Route
                          key={`teacher-child-${index}-${childIndex}`}
                          path={child.path}
                          element={child.component}
                          index={child.index}
                        />
                      ))}
                    </Route>
                  ),
                )}
              </Route>
            )}
            
            { session &&
              <Route
                element={
                  <MasterLayout>
                    <SharedLayout />
                  </MasterLayout>
                }
              >
                {appRoutes.map((r, index) => (
                  <Route key={"shared" + index} path={r.path} element={r.component} />
                ))}
              </Route>
            }


            <Route path="/not-found" element={<NotFound />} />
            { session && <Route path="*" element={<NotFound />} /> }

          </>
        )}
      </Routes>
    </Suspense>
  );
};

export default Routing;
